/**
 * 安全地将错误对象转换为字符串，处理循环引用和不可序列化的值
 * @param error - 要序列化的错误对象或任何值
 * @param space - JSON.stringify 的 space 参数，用于格式化输出
 * @returns 安全的字符串表示
 */
export function safeStringifyError(error: unknown, space?: string | number): string {
  try {
    // 如果是简单类型，直接转换
    if (error === null || error === undefined) {
      return String(error);
    }
    
    if (typeof error === 'string' || typeof error === 'number' || typeof error === 'boolean') {
      return String(error);
    }

    // 如果是 Error 对象，提取关键信息
    if (error instanceof Error) {
      const errorInfo = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        // 包含其他可能的属性
        ...getSerializableProps(error),
      };
      return JSON.stringify(errorInfo, null, space);
    }

    // 对于其他对象，使用安全的序列化
    return JSON.stringify(error, createSafeReplacer(), space);
  } catch (e) {
    // 如果 JSON.stringify 仍然失败，返回基本信息
    try {
      if (error instanceof Error) {
        return `Error: ${error.name} - ${error.message}`;
      }
      return `[Object: ${typeof error}] ${Object.prototype.toString.call(error)}`;
    } catch {
      return `[Unserializable object of type: ${typeof error}]`;
    }
  }
}

/**
 * 创建一个安全的 JSON.stringify replacer 函数
 * 处理循环引用、BigInt、Symbol 等不可序列化的值
 */
function createSafeReplacer() {
  const seen = new WeakSet();
  
  return function(_key: string, value: any) {
    // 处理循环引用
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
    }
    
    // 处理不可序列化的类型
    switch (typeof value) {
      case 'undefined':
        return '[undefined]';
      case 'symbol':
        return `[Symbol: ${value.toString()}]`;
      case 'function':
        return `[Function: ${value.name || 'anonymous'}]`;
      case 'bigint':
        return `[BigInt: ${value.toString()}]`;
      default:
        return value;
    }
  };
}

/**
 * 获取对象的可序列化属性
 * @param obj - 要检查的对象
 * @returns 包含可序列化属性的对象
 */
function getSerializableProps(obj: any): Record<string, any> {
  const result: Record<string, any> = {};
  
  try {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        // 跳过函数和不可序列化的值
        if (typeof value !== 'function' && key !== 'stack' && key !== 'name' && key !== 'message') {
          try {
            JSON.stringify(value);
            result[key] = value;
          } catch {
            result[key] = `[Unserializable: ${typeof value}]`;
          }
        }
      }
    }
  } catch {
    // 如果遍历属性失败，返回空对象
  }
  
  return result;
}

/**
 * 简化版本的安全字符串化，只处理最常见的情况
 * @param value - 要序列化的值
 * @returns 安全的字符串表示
 */
export function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, createSafeReplacer());
  } catch {
    return String(value);
  }
}
